/* eslint-disable complexity */
/* eslint-disable react/display-name */
/* eslint-disable no-console */
import React, { useEffect, useState, useMemo, useCallback, useRef, ChangeEvent } from 'react';
import DropZone from 'react-scale/corp/components/DropZone';
import SanitizedMarkdown from 'react-scale/components/design-system/SanitizedMarkdown';
import { Button } from 'design-system/Form';
import Dialog, { DialogContent, DialogTitle } from 'design-system/Dialog';
import Table from 'corp/components/UI/Table';
import { TextInput, Text, Checkbox, Switch } from 'design-system';
import apiFetch, { defaultApiFetchErrorHandler } from 'react-scale/lib/api-fetch';
import { requiredAsterisk as defaultAsterisk } from 'react-scale/corp/helpers';
import FileSaver from 'file-saver';

/**
 * TODO:
 * - Add description and/or link to doc
 * - Add metadata flag to projects and use it to pull and fill a dropdown select, instead of the freetext project input
 * - Replace table with a hiercharchy view for the current files in bucket
 */

const description = `
### Description:
- For help, go to #bxok-fde-help
- Files with the same path will be overwritten
- Files with less than 100,000 objects will be converted to JSON
---
`;

const getBrowserRelativePath = (file: File) => (file as any).webkitRelativePath || file.name;
const isDsStoreFile = (file: File) =>
  getBrowserRelativePath(file).split('/').pop()?.toLowerCase() === '.ds_store';
const FOLDER_UPLOAD_MAX_BATCH_BYTES = 45 * 1024 * 1024;
const FOLDER_UPLOAD_MAX_BATCH_FILES = 200;

const buildFolderUploadBatches = (files: File[]) => {
  const batches: File[][] = [];
  let currentBatch: File[] = [];
  let currentBatchBytes = 0;

  files.forEach(file => {
    const exceedsBatchLimits =
      currentBatch.length >= FOLDER_UPLOAD_MAX_BATCH_FILES ||
      currentBatchBytes + file.size > FOLDER_UPLOAD_MAX_BATCH_BYTES;

    if (currentBatch.length > 0 && exceedsBatchLimits) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBatchBytes = 0;
    }

    currentBatch.push(file);
    currentBatchBytes += file.size;
  });

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

const UploadTasksToCustomer = () => {
  const [loadingCurrentFiles, setLoadingCurrentFiles] = useState<boolean>(false);
  const [analyzingFile, setAnalyzingFile] = useState<boolean>(false);
  const [uploadingFiles, setUploadingFiles] = useState<boolean>(false);
  const [downloadingFiles, setDownloadingFiles] = useState<boolean>(false);
  const [parsingFile, setParsingFile] = useState<boolean>(false);
  const [currentFilesRows, setCurrentFilesRows] = useState<{ path: string }[]>([]);
  const [newFilesRows, setNewFilesRows] = useState<{ path: string; status: string }[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [project, setProject] = useState<string>('');
  const [datestamp, setDatestamp] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [uploadId, setUploadId] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [hideArchived, setHideArchived] = useState<boolean>(true);
  const [archivingFiles, setArchivingFiles] = useState<boolean>(false);
  const [filteredRows, setFilteredRows] = useState<{ path: string }[]>([]);
  const [lastSelectedPath, setLastSelectedPath] = useState<string | null>(null);
  const [oldPath, setOldPath] = useState<string | null>(null);
  const [newPath, setNewPath] = useState<string>('');
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [originalPrefix, setOriginalPrefix] = useState<string>('');
  const [fileNameMatch, setFileNameMatch] = useState<string>('');
  const [newPrefix, setNewPrefix] = useState<string>('');
  const [migrating, setMigrating] = useState<boolean>(false);
  const [deletingArchivedFiles, setDeletingArchivedFiles] = useState<boolean>(false);
  const [useOverride, setUseOverride] = useState<boolean>(false);
  const [overridePath, setOverridePath] = useState<string>('');
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const [folderDestinationPrefix, setFolderDestinationPrefix] = useState<string>('');
  const [uploadingFolder, setUploadingFolder] = useState<boolean>(false);
  const [folderUploadMode, setFolderUploadMode] = useState<'staging' | 'customer'>('staging');
  const [skipDsStoreFiles, setSkipDsStoreFiles] = useState<boolean>(true);
  const [stagingTargetBucket, setStagingTargetBucket] = useState<string>('');
  const [stagingTargetPrefix, setStagingTargetPrefix] = useState<string>('');
  const [customerTargetBucket, setCustomerTargetBucket] = useState<string>('');
  const [customerTargetPrefix, setCustomerTargetPrefix] = useState<string>('');
  const [folderTargetLoaded, setFolderTargetLoaded] = useState<boolean>(false);
  const [folderUploadResults, setFolderUploadResults] = useState<
    { path: string; status: string }[]
  >([]);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const [duplicates, setDuplicates] = useState<
    Array<{
      taskId: string;
      previousDeliveryDate: string;
      previousDeliveryFile: string;
    }>
  >([]);
  const [daysBack, setDaysBack] = useState<number>(0);
  const displayedRowsRef = useRef<{ path: string }[]>([]);
  const filteredFolderFiles = useMemo(
    () => (skipDsStoreFiles ? folderFiles.filter(f => !isDsStoreFile(f)) : folderFiles),
    [folderFiles, skipDsStoreFiles],
  );
  const activeTargetBucket =
    folderUploadMode === 'staging' ? stagingTargetBucket : customerTargetBucket;
  const activeTargetPrefix =
    folderUploadMode === 'staging' ? stagingTargetPrefix : customerTargetPrefix;

  const handleSelectAll = useCallback(
    (data: { path: string }[]) => {
      const filteredPaths = data.map(row => row.path);
      const allFilteredSelected =
        filteredPaths.length > 0 && filteredPaths.every(path => selectedFiles.includes(path));
      if (allFilteredSelected) {
        setSelectedFiles(prev => prev.filter(path => !filteredPaths.includes(path)));
      } else {
        setSelectedFiles(prev => [...new Set([...prev, ...filteredPaths])]);
      }
    },
    [selectedFiles],
  );

  const handleCheckboxChange = useCallback(
    (path: string, isShiftClick = false) => {
      const displayedRows = displayedRowsRef.current;
      if (isShiftClick && lastSelectedPath) {
        const lastIndex = displayedRows.findIndex(row => row.path === lastSelectedPath);
        const currentIndex = displayedRows.findIndex(row => row.path === path);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const [start, end] = [lastIndex, currentIndex].sort((a, b) => a - b);
          const pathsInRange = displayedRows.slice(start, end + 1).map(row => row.path);

          const isChecking = !selectedFiles.includes(path);

          if (isChecking) {
            setSelectedFiles(prev => [...new Set([...prev, ...pathsInRange])]);
          } else {
            setSelectedFiles(prev => prev.filter(p => !pathsInRange.includes(p)));
          }
        } else {
          setSelectedFiles(prev =>
            prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path],
          );
        }
      } else {
        setSelectedFiles(prev =>
          prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path],
        );
      }

      setLastSelectedPath(path);
    },
    [lastSelectedPath, selectedFiles],
  );

  const filesTableColumns = useMemo(() => {
    const SelectAllHeader = ({ data }: { data: { path: string }[] }) => {
      displayedRowsRef.current = data;
      const filteredPaths = data.map(row => row.path);
      const allFilteredSelected =
        filteredPaths.length > 0 && filteredPaths.every(path => selectedFiles.includes(path));

      return (
        <input
          type="checkbox"
          checked={allFilteredSelected}
          onChange={() => handleSelectAll(data)}
        />
      );
    };

    return [
      {
        Header: SelectAllHeader,
        accessor: 'path',
        Cell: ({ value }: { value: string }) => (
          <input
            type="checkbox"
            checked={selectedFiles.includes(value)}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              handleCheckboxChange(value, (event.nativeEvent as MouseEvent).shiftKey)
            }
          />
        ),
        width: 50,
        id: 'selection',
      },
      {
        accessor: 'path',
        filterable: true,
        Header: 'File Path',
        sorteable: true,
        filterMethod: (filter: { value: string }, row: { path: string }) => {
          return row.path
            ?.toString()
            .toLowerCase()
            .includes(filter.value?.toString().toLowerCase());
        },
      },
      {
        Header: 'Actions',
        accessor: 'path',
        Cell: ({ value }: { value: string }) => (
          <Button size="sm" onClick={() => handleEditPath(value)}>
            Edit
          </Button>
        ),
        width: 100,
      },
    ];
  }, [selectedFiles, handleSelectAll, handleCheckboxChange]);

  const handleFileUpload = async (file: File) => {
    if (useOverride) {
      return;
    }
    setParsingFile(true);
    const fileContent = await file.text();
    const fileData = JSON.parse(fileContent);

    if (!Array.isArray(fileData)) {
      console.error('Invalid file format');
      setParsingFile(false);
      return;
    }

    const taskId = fileData[0].deliverable_id;
    try {
      const response = await apiFetch(null, `corp-api/bee-rlhf-utils/kratos-autofill/${taskId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      setProject(response.projectPrefix);
      setFilename(response.fileName);
    } catch (error) {
      console.error('Error fetching project info:', error);
    }
    setParsingFile(false);
  };

  const handleDrop = (files: File[]) => {
    setFiles(files);
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const uploadTableColumns = [
    {
      accessor: 'path',
      filterable: true,
      Header: 'Path',
      sorteable: true,
      filterMethod: (filter: { value: string }, row: { path: string }) => {
        return row.path?.toString().toLowerCase().includes(filter.value?.toString().toLowerCase());
      },
    },
    {
      accessor: 'status',
      filterable: true,
      Header: 'Status',
      sorteable: true,
      filterMethod: (filter: { value: string }, row: { status: string }) => {
        return row.status
          ?.toString()
          .toLowerCase()
          .includes(filter.value?.toString().toLowerCase());
      },
    },
  ];

  const handleEditPath = (path: string) => {
    setOldPath(path);
    setNewPath(path);
    setIsEditModalOpen(true);
  };

  async function fetchCurrentFiles() {
    setLoadingCurrentFiles(true);
    setCurrentFilesRows([]);
    try {
      const res = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files', {
        method: 'GET',
      });

      setCurrentFilesRows(res.map((x: string) => ({ path: x })));
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setLoadingCurrentFiles(false);
  }

  async function analyzeFile() {
    setAnalyzingFile(true);
    setUploadId('');
    setNewFilesRows([]);
    setDuplicates([]);
    try {
      const data = new FormData();
      data.append('project', project);
      data.append('datestamp', datestamp);
      data.append('filename', filename.trim());
      files.forEach(file => data.append('file', file));

      const res = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/analyze', {
        method: 'POST',
        body: data,
      });

      setUploadId(res.uploadId);
      setNewFilesRows(
        res.result.map((x: { path: string; msg: string }) => {
          let status = x.msg;
          const existingFile = currentFilesRows.find(row => row.path === x.path);
          if (existingFile) {
            status += ', ⚠️ will overwrite';
          }
          if (
            files[0].name.toLowerCase().endsWith('.jsonl') &&
            x.path.toLowerCase().endsWith('.json')
          ) {
            status += ', converted to JSON';
          }
          return { path: x.path, status };
        }),
      );

      // Set duplicates if any were found during parsing
      if (res.duplicates && res.duplicates.length > 0) {
        setDuplicates(res.duplicates);
      }
      if (res.daysBack) {
        setDaysBack(res.daysBack);
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setAnalyzingFile(false);
  }

  async function uploadFiles() {
    setUploadingFiles(true);
    setNewFilesRows(
      newFilesRows.map(x => {
        return { path: x.path, status: '⏱️ Uploading...' };
      }),
    );
    try {
      const res = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files', {
        method: 'POST',
        body: { uploadId },
      });

      setNewFilesRows(
        res.result.map((x: { path: string; msg: string }) => {
          return { path: x.path, status: x.msg };
        }),
      );
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setUploadingFiles(false);
  }

  async function downloadFiles(paths: string[]) {
    setDownloadingFiles(true);
    try {
      const response = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/download', {
        method: 'POST',
        body: JSON.stringify({ paths }),
        headers: {
          'Content-Type': 'application/json',
        },
        returnRawResponse: true,
      });

      if (!response || !response.body) {
        throw new Error('Failed to download files: empty response');
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const stream = new ReadableStream({
        start(controller) {
          function push() {
            reader
              .read()
              .then(({ done, value }: { done: boolean; value: Uint8Array }) => {
                if (done) {
                  controller.close();
                  return;
                }
                controller.enqueue(value);
                return push();
              })
              .catch((err: any) => {
                controller.error(err);
              });
          }
          push();
        },
      });

      const blob = await new Response(stream).blob();
      FileSaver.saveAs(blob, 'bee_customer_files.zip');
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setDownloadingFiles(false);
  }

  async function archiveFiles(paths: string[]) {
    setArchivingFiles(true);
    try {
      await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/archive', {
        method: 'POST',
        body: JSON.stringify({ paths }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
      await fetchCurrentFiles();
      setSelectedFiles([]);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setArchivingFiles(false);
  }

  const handleUpdatePath = async () => {
    if (!oldPath || !newPath) return;
    setIsUpdating(true);

    try {
      const response = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/update-path', {
        method: 'POST',
        body: JSON.stringify({ oldPath, newPath }),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.message) {
        fetchCurrentFiles();
        setIsEditModalOpen(false);
        setOldPath(null);
        setNewPath('');
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setIsUpdating(false);
  };

  const handlePrefixMigrate = async () => {
    setMigrating(true);
    try {
      await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/bulk-update-prefix', {
        method: 'POST',
        body: JSON.stringify({
          originalPrefix,
          fileNameMatch: fileNameMatch.trim(),
          newPrefix,
        }),
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setMigrating(false);
    await fetchCurrentFiles();
  };

  useEffect(() => {
    const newFilteredRows = currentFilesRows.filter(
      row => !hideArchived || !row.path.toLowerCase().startsWith('archive'),
    );
    setFilteredRows(newFilteredRows);
    displayedRowsRef.current = newFilteredRows;
  }, [currentFilesRows, hideArchived]);

  useEffect(() => {
    async function fetchFolderUploadTarget() {
      try {
        const res = await apiFetch(
          null,
          'corp-api/bee-rlhf-utils/customer-files/upload-folder-target',
          {
            method: 'GET',
          },
        );
        setStagingTargetBucket(res.targets?.staging?.bucket || '');
        setStagingTargetPrefix(res.targets?.staging?.prefix || '');
        setCustomerTargetBucket(res.targets?.customer?.bucket || '');
        setCustomerTargetPrefix(res.targets?.customer?.prefix || '');
        setFolderTargetLoaded(true);
      } catch (error) {
        defaultApiFetchErrorHandler(error, { handleHttp: true });
        setFolderTargetLoaded(false);
      }
    }
    fetchFolderUploadTarget();
  }, []);

  function downloadUploadedFilesButtonText() {
    if (downloadingFiles) return 'Downloading...';
    const uploadedCount = newFilesRows.filter(row => row.status.includes('Uploaded')).length;
    return `Download ${uploadedCount} Uploaded File${uploadedCount !== 1 ? 's' : ''}`;
  }

  function archiveFilesButtonText() {
    if (archivingFiles) return 'Archiving...';
    return `Archive ${selectedFiles.length} Selected File${selectedFiles.length !== 1 ? 's' : ''}`;
  }

  function fetchCurrentFilesButtonText() {
    if (loadingCurrentFiles) return 'Fetching...';
    return 'Fetch current files';
  }

  function analyzeFileButtonText() {
    if (analyzingFile) return 'Analyzing...';
    if (parsingFile) return 'Parsing...';
    if (!project) return 'Project Name is required';
    if (!datestamp) return 'Datestamp is required';
    if (!filename) return 'File Name is required';
    if (!files.length) return 'File is required';
    return 'Analyze File';
  }

  function uploadFilesButtonText() {
    if (uploadingFiles) return 'Uploading...';
    if (!uploadId) return 'Upload ID is required';
    if (!newFilesRows.length) return 'Files are required';
    return 'Upload Files';
  }

  async function deleteArchivedFiles() {
    setDeletingArchivedFiles(true);
    try {
      await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/delete-archived', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      await fetchCurrentFiles();
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setDeletingArchivedFiles(false);
  }

  function deleteArchivedFilesButtonText() {
    if (deletingArchivedFiles) return 'Deleting...';
    return 'Delete Archived Files';
  }

  const handleFolderSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? []);
    setFolderFiles(selected);
    setFolderUploadResults([]);
    // Reset so re-selecting the same folder triggers onChange again
    event.target.value = '';
  };

  async function uploadFolder() {
    if (!folderDestinationPrefix || filteredFolderFiles.length === 0) return;
    setUploadingFolder(true);
    const getRelativePath = (f: File) => getBrowserRelativePath(f);

    const normalizedPrefix = folderDestinationPrefix.replace(/\/+$/, '').trim();
    setFolderUploadResults(
      filteredFolderFiles.map(f => ({
        path: `${normalizedPrefix}/${getRelativePath(f)}`,
        status: '⏳ Queued...',
      })),
    );

    try {
      const batches = buildFolderUploadBatches(filteredFolderFiles);
      const statusByPath = new Map<string, string>(
        filteredFolderFiles.map(file => [
          `${normalizedPrefix}/${getRelativePath(file)}`,
          '⏳ Queued...',
        ]),
      );

      for (let i = 0; i < batches.length; i += 1) {
        const batch = batches[i];
        const batchRelativePaths = batch.map(getRelativePath);

        batchRelativePaths.forEach(relativePath => {
          statusByPath.set(
            `${normalizedPrefix}/${relativePath}`,
            `⏱️ Uploading batch ${i + 1}/${batches.length}...`,
          );
        });
        setFolderUploadResults(
          filteredFolderFiles.map(file => ({
            path: `${normalizedPrefix}/${getRelativePath(file)}`,
            status:
              statusByPath.get(`${normalizedPrefix}/${getRelativePath(file)}`) || '⏳ Queued...',
          })),
        );

        const data = new FormData();
        data.append('destinationPrefix', normalizedPrefix);
        data.append('uploadMode', folderUploadMode);
        data.append('relativePaths', JSON.stringify(batchRelativePaths));
        batch.forEach(file => data.append('files', file));

        const res = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/upload-folder', {
          method: 'POST',
          body: data,
        });

        const batchResultByRelativePath = new Map<string, string>();
        const s3PathPrefix = `s3://${activeTargetBucket}/${activeTargetPrefix}`;
        (res.result as { path: string; msg: string }[]).forEach(item => {
          const relativePath = item.path.startsWith(s3PathPrefix)
            ? item.path.slice(s3PathPrefix.length)
            : item.path;
          batchResultByRelativePath.set(relativePath, item.msg);
        });

        batchRelativePaths.forEach(relativePath => {
          const fullRelativePath = `${normalizedPrefix}/${relativePath}`;
          statusByPath.set(
            fullRelativePath,
            batchResultByRelativePath.get(fullRelativePath) || '✅ Uploaded',
          );
        });

        setFolderUploadResults(
          filteredFolderFiles.map(file => ({
            path: `${normalizedPrefix}/${getRelativePath(file)}`,
            status:
              statusByPath.get(`${normalizedPrefix}/${getRelativePath(file)}`) || '⏳ Queued...',
          })),
        );
      }
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
      setFolderUploadResults(prev =>
        prev.map(row =>
          row.status.startsWith('✅') || row.status.startsWith('❌')
            ? row
            : { ...row, status: '❌ Upload interrupted' },
        ),
      );
    }
    setUploadingFolder(false);
  }

  function uploadFolderButtonText() {
    if (uploadingFolder) return 'Uploading...';
    if (!folderDestinationPrefix) return 'Destination prefix is required';
    if (filteredFolderFiles.length === 0) return 'No files to upload';
    return `Upload ${filteredFolderFiles.length} file${
      filteredFolderFiles.length !== 1 ? 's' : ''
    }`;
  }

  async function uploadOverrideFile() {
    setUploadingFiles(true);
    try {
      const data = new FormData();
      data.append('path', overridePath);
      data.append('file', files[0]);

      const res = await apiFetch(null, 'corp-api/bee-rlhf-utils/customer-files/upload-override', {
        method: 'POST',
        body: data,
      });

      setNewFilesRows([{ path: res.path, status: res.msg }]);
    } catch (error) {
      defaultApiFetchErrorHandler(error, { handleHttp: true });
    }
    setUploadingFiles(false);
  }

  function uploadOverrideButtonText() {
    if (uploadingFiles) return 'Uploading...';
    if (useOverride) {
      if (!overridePath) return 'Path is required';
      if (!files.length) return 'File is required';
      return 'Upload File';
    }
    return uploadFilesButtonText();
  }

  return (
    <div className="root">
      <section>
        <SanitizedMarkdown text={description} />
      </section>

      <section>
        <h3>Current files on customer bucket</h3>
        <Checkbox
          checked={hideArchived}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setHideArchived(event.target.checked)
          }
          label="Hide archived files"
        />
        {/* @ts-expect-error @types/react@18 $FIXME */}
        <Table style={{ maxWidth: '1000px' }} columns={filesTableColumns} data={filteredRows} />
        <br />
        <Button
          primary
          disabled={loadingCurrentFiles}
          requireConfirmation={false}
          onClick={fetchCurrentFiles}
        >
          {fetchCurrentFilesButtonText()}
        </Button>
        <Button
          primary
          disabled={selectedFiles.length === 0 || downloadingFiles}
          onClick={() => downloadFiles(selectedFiles)}
          style={{ marginLeft: '10px' }}
        >
          {downloadingFiles
            ? 'Downloading...'
            : `Download ${selectedFiles.length} Selected File${
                selectedFiles.length !== 1 ? 's' : ''
              }`}{' '}
        </Button>
        <Button
          primary
          disabled={selectedFiles.length === 0 || archivingFiles}
          onClick={() => archiveFiles(selectedFiles)}
          style={{ marginLeft: '10px' }}
        >
          {archiveFilesButtonText()}
        </Button>
        <Button
          primary
          disabled={deletingArchivedFiles}
          onClick={deleteArchivedFiles}
          style={{ marginLeft: '10px' }}
        >
          {deleteArchivedFilesButtonText()}
        </Button>
        <br />
        <h3>Migrate Files to New Prefix</h3>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px', maxWidth: '1000px' }}>
          <div>
            <div className="flex flex-col">
              <Text medium>Original Prefix</Text>
              <div>Exact match, do not include /</div>
            </div>
            <TextInput
              value={originalPrefix}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value.replace(/\//g, '');
                setOriginalPrefix(newValue.trim());
              }}
              style={{ width: '240px' }}
            />
          </div>
          <div>
            <div className="flex flex-col">
              <Text medium>File Name Regex Match</Text>
              <div>Case insensitive. Leave blank for all</div>
            </div>
            <TextInput
              value={fileNameMatch}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFileNameMatch(event.target.value)
              }
              style={{ width: '240px' }}
            />
          </div>
          <div>
            <div className="flex flex-col">
              <Text medium>New Prefix</Text>
              <div>Exact match, do not include /</div>
            </div>
            <TextInput
              value={newPrefix}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newValue = event.target.value.replace(/\//g, '');
                setNewPrefix(newValue.trim());
              }}
              style={{ width: '240px' }}
            />
          </div>
          <Button
            primary
            disabled={!originalPrefix || !newPrefix || migrating}
            onClick={handlePrefixMigrate}
          >
            {migrating ? 'Migrating...' : 'Migrate'}
          </Button>
        </div>
      </section>
      <br />
      <hr />

      <section>
        <h3>Upload new files to customer bucket</h3>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '16px',
            marginLeft: '16px',
          }}
        >
          <Switch
            checked={useOverride}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setUseOverride(event.target.checked)
            }
          />
          <Text>Override Upload (ENG ONLY)</Text>
        </div>
        {useOverride ? (
          <>
            <div>
              <Text medium>File Path</Text> {defaultAsterisk}
            </div>
            <TextInput
              value={overridePath}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOverridePath(event.target.value.trim());
              }}
            />
          </>
        ) : (
          <>
            <div>
              <Text medium>Project Prefix</Text> {defaultAsterisk}
            </div>
            <div>
              <Text>
                Please refer to the{' '}
                <a
                  href="https://docs.google.com/document/d/1bbBvecxtD5eI5dnH-iRFaaCSzWH6DrQZToacdI3GQfw/edit#heading=h.qzvc6pqp5jml"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  runbook
                </a>{' '}
                for your project prefix.
              </Text>
            </div>
            <TextInput
              value={project}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setProject(event.target.value.trim());
              }}
              style={{ width: '600px' }}
            />
            <br />
            <br />
            <div className="textTitle">
              <Text medium>Datestamp</Text> {defaultAsterisk}
            </div>
            <TextInput
              value={datestamp}
              placeholder="YYYYMMDD"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setDatestamp(event.target.value.trim());
              }}
            />
            <br />
            <br />
            <div className="textTitle">
              <Text medium>File Name</Text> {defaultAsterisk}
            </div>
            <TextInput
              value={filename}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setFilename(event.target.value);
              }}
              style={{ width: '600px' }}
            />
          </>
        )}
        <br />
        <br />
        <div className="textTitle">
          <Text medium>File to upload</Text> {defaultAsterisk}
        </div>
        <DropZone
          width={200}
          height={150}
          overrideLabel={'Drop your file'}
          handleDrop={handleDrop}
          dropzoneParams={{ multiple: false }}
        />
        <br />
        {files.length ? (
          <>
            <strong>{files[0].name}</strong>
            <br />
            <br />
          </>
        ) : (
          ''
        )}
        <Button
          primary
          disabled={
            useOverride
              ? !overridePath || !files.length || uploadingFiles
              : !project || !datestamp || !filename || !files.length || analyzingFile || parsingFile
          }
          requireConfirmation={false}
          onClick={useOverride ? uploadOverrideFile : analyzeFile}
        >
          {useOverride ? uploadOverrideButtonText() : analyzeFileButtonText()}
        </Button>
        {newFilesRows.length ? (
          <>
            <br />
            <br />
            <Table
              style={{ maxWidth: '1000px' }}
              columns={uploadTableColumns}
              data={newFilesRows}
            />
            {duplicates.length > 0 && (
              <>
                <br />
                <div
                  style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    padding: '16px',
                    maxWidth: '1000px',
                  }}
                >
                  <p style={{ fontWeight: 'bold', color: '#856404', marginBottom: '8px' }}>
                    ⚠️ Warning: {duplicates.length} duplicate task(s) found in previously delivered
                    files (last {daysBack} days)
                  </p>
                  <Table
                    style={{ maxWidth: '100%', backgroundColor: 'white' }}
                    columns={[
                      { Header: 'Task ID', accessor: 'taskId' },
                      { Header: 'Previous Delivery Date', accessor: 'previousDeliveryDate' },
                      { Header: 'Previous Delivery File', accessor: 'previousDeliveryFile' },
                    ]}
                    data={duplicates}
                  />
                </div>
              </>
            )}
            <br />
            <Button
              primary
              disabled={!uploadId || !newFilesRows.length || uploadingFiles}
              requireConfirmation={true}
              onClick={uploadFiles}
            >
              {uploadFilesButtonText()}
            </Button>
            <Button
              primary
              disabled={
                downloadingFiles ||
                newFilesRows.filter(row => row.status.includes('Uploaded')).length === 0
              }
              requireConfirmation={false}
              onClick={() =>
                downloadFiles(
                  newFilesRows.filter(row => row.status.includes('Uploaded')).map(row => row.path),
                )
              }
              style={{ marginLeft: '10px' }}
            >
              {downloadUploadedFilesButtonText()}
            </Button>
          </>
        ) : (
          ''
        )}
      </section>
      <br />
      <hr />

      <section>
        <h3>Upload Folder to Customer Bucket</h3>
        <p>
          Upload an entire local folder preserving its directory structure under a destination S3
          prefix.
        </p>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            marginLeft: '16px',
          }}
        >
          <Switch
            checked={folderUploadMode === 'customer'}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setFolderUploadMode(event.target.checked ? 'customer' : 'staging')
            }
          />
          <Text>
            Upload mode: <strong>{folderUploadMode === 'staging' ? 'Staging' : 'Customer'}</strong>
          </Text>
        </div>
        <div>
          <Text medium>Destination Prefix</Text> {defaultAsterisk}
        </div>
        <div>
          <Text>
            The folder will be placed under this prefix, e.g. <code>project-x/2024/</code>
          </Text>
          <br />
          <Text>
            Path behavior: if prefix is <code>some/path/</code> and you select folder{' '}
            <code>folder1</code>, a file like <code>subdir/file.json</code> becomes{' '}
            <code>some/path/folder1/subdir/file.json</code>.
          </Text>
          <br />
          <Text>
            Uploading to bucket:{' '}
            {folderTargetLoaded ? (
              <>
                <strong>
                  <code>{activeTargetBucket}</code>
                </strong>
              </>
            ) : (
              <code>Loading configured bucket...</code>
            )}
          </Text>
        </div>
        <TextInput
          value={folderDestinationPrefix}
          placeholder="e.g. project-x/2024/"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setFolderDestinationPrefix(event.target.value.trim())
          }
          style={{ width: '600px' }}
        />
        <br />
        <br />
        {/* Hidden folder input — webkitdirectory is non-standard but widely supported */}
        <input
          ref={folderInputRef}
          type="file"
          {...({ webkitdirectory: '' } as any)}
          multiple
          style={{ display: 'none' }}
          onChange={handleFolderSelect}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Button
            primary
            requireConfirmation={false}
            onClick={() => folderInputRef.current?.click()}
          >
            Select Folder
          </Button>
          <Checkbox
            checked={skipDsStoreFiles}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setSkipDsStoreFiles(event.target.checked)
            }
            label="Skip .DS_Store files"
          />
        </div>
        {filteredFolderFiles.length > 0 && (
          <div style={{ marginTop: '8px' }}>
            <Text>
              Uploading <strong>{filteredFolderFiles.length}</strong> total file
              {filteredFolderFiles.length !== 1 ? 's' : ''} from{' '}
              <strong>{getBrowserRelativePath(filteredFolderFiles[0]).split('/')[0]}</strong>
            </Text>
          </div>
        )}
        {folderFiles.length > filteredFolderFiles.length && (
          <div style={{ marginTop: '8px' }}>
            <Text>
              Skipping <strong>{folderFiles.length - filteredFolderFiles.length}</strong>{' '}
              <code>.DS_Store</code> file
              {folderFiles.length - filteredFolderFiles.length !== 1 ? 's' : ''}.
            </Text>
          </div>
        )}
        {folderDestinationPrefix && filteredFolderFiles[0] && (
          <div style={{ marginTop: '8px' }}>
            <Text>
              First file preview:{' '}
              {folderTargetLoaded ? (
                <code>
                  {`s3://${activeTargetBucket}/${activeTargetPrefix}${folderDestinationPrefix
                    .replace(/\/+$/, '')
                    .trim()}/${getBrowserRelativePath(filteredFolderFiles[0])}`}
                </code>
              ) : (
                <code>Waiting for configured bucket...</code>
              )}
            </Text>
          </div>
        )}
        <br />
        <br />
        <Button
          primary
          disabled={!folderDestinationPrefix || filteredFolderFiles.length === 0 || uploadingFolder}
          requireConfirmation={true}
          onClick={uploadFolder}
        >
          {uploadFolderButtonText()}
        </Button>
        {folderUploadResults.length > 0 && (
          <>
            <br />
            <br />
            <Table
              style={{ maxWidth: '1000px' }}
              columns={uploadTableColumns}
              data={folderUploadResults}
            />
          </>
        )}
      </section>
      <br />

      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit File Path</DialogTitle>
        <DialogContent>
          <TextInput
            value={newPath}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNewPath(event.target.value)
            }
            style={{ width: '100%', marginBottom: '16px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button primary onClick={handleUpdatePath} disabled={isUpdating}>
              {isUpdating ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UploadTasksToCustomer;
